import { API } from 'shared/utils/apiMiddleware';
import {
  GovernanceTagParams,
  GovernanceTagDistinctValues,
  TagSearchParams,
  ResourceFilter,
  FilterParams,
  BulkDeleteResult,
  FlatResourcesQueryParams,
  PaginatedResourcesResponse,
  ResourceData,
  GovernanceTag,
} from '../types';

class GovernanceTagsApi {
  private readonly rootPath = '/api/v2/governance-tags';
  private readonly rootPathV1 = '/api/v1/invoices/service-costs/distinct-tags/governance';
  private transformResourceData = (backendResource: Record<string, unknown>): ResourceData => {
    return {
      id: String(backendResource.id || ''),
      resourceCost: Number(backendResource.resourceCost) || 0,
      resourceName: String(backendResource.resourceName || ''),
      resourceId: String(backendResource.resourceId || ''),
      service: String(backendResource.service || ''),
      region: String(backendResource.region || ''),
      linkedAccount: String(backendResource.linkedAccount || ''),
      tags: (backendResource.tags as Record<string, string>) || {},
      governanceTags: (backendResource.governanceTags as GovernanceTag[]) || [],
      lastUpdate: String(backendResource.lastUpdate || ''),
    };
  };

  private buildFilterParams = (filterParams?: FilterParams): URLSearchParams => {
    const params = new URLSearchParams();

    if (filterParams) {
      Object.entries(filterParams).forEach(([key, values]) => {
        if (values && values.length > 0) {
          values.forEach((value) => {
            params.append(key, value);
          });
        }
      });
    }

    return params;
  };

  private buildFlatFilterParams = (queryParams: FlatResourcesQueryParams): URLSearchParams => {
    const params = new URLSearchParams();

    const filterFields = ['service', 'region', 'resourceName', 'resourceid', 'linkedaccid', 'tags', 'governancetags'];

    filterFields.forEach((field) => {
      const values = queryParams[field as keyof FlatResourcesQueryParams] as string[] | undefined;
      if (values && Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          params.append(field, value);
        });
      }
    });

    // Handle operation-based filters
    Object.keys(queryParams).forEach((key) => {
      if (key.includes('_') && (key.startsWith('resourceCost_') || key.startsWith('lastUpdate_'))) {
        const values = queryParams[key as keyof FlatResourcesQueryParams] as string[] | undefined;
        if (values && Array.isArray(values) && values.length > 0) {
          values.forEach((value) => {
            params.append(key, value);
          });
        }
      }
    });

    if (queryParams.searchText) {
      params.append('searchText', queryParams.searchText);
    }

    if (queryParams.filter) {
      params.append('filter', queryParams.filter);
    }

    if (queryParams.page) {
      params.append('page', queryParams.page.toString());
    }

    if (queryParams.pageSize) {
      params.append('pageSize', queryParams.pageSize.toString());
    }

    if (queryParams.sortBy && queryParams.sortBy.length > 0) {
      params.append('sortBy', JSON.stringify(queryParams.sortBy));
    }

    return params;
  };

  getResources = async (filter?: ResourceFilter, filterParams?: FilterParams): Promise<ResourceData[]> => {
    const params = this.buildFilterParams(filterParams);
    if (filter) params.append('filter', filter);

    const queryString = params.toString();
    const endpoint = `${this.rootPath}/resources${queryString ? `?${queryString}` : ''}`;

    const response = await API.get('billings', endpoint);

    return response.resources?.map(this.transformResourceData) ?? [];
  };

  getPaginatedResources = async (queryParams: FlatResourcesQueryParams): Promise<PaginatedResourcesResponse> => {
    const params = this.buildFlatFilterParams(queryParams);
    const queryString = params.toString();
    const endpoint = `${this.rootPath}/resources${queryString ? `?${queryString}` : ''}`;

    const response = await API.get('billings', endpoint);

    return {
      ...response,
      resources: response.resources?.map(this.transformResourceData) ?? [],
    };
  };

  getGovernanceMetrics = async () => {
    return API.get('billings', `${this.rootPath}/metrics`);
  };

  getDistinctTagValues = async (params: TagSearchParams = {}): Promise<GovernanceTagDistinctValues> => {
    const urlParams = new URLSearchParams();
    if (params.tagKey) urlParams.append('tagKey', params.tagKey);
    if (params.searchStr) urlParams.append('searchStr', params.searchStr);

    const queryString = urlParams.toString();
    const endpoint = `${this.rootPathV1}${queryString ? `?${queryString}` : ''}`;

    return API.get('billings', endpoint);
  };

  getDistinctTagKeys = async (params: TagSearchParams = {}): Promise<GovernanceTagDistinctValues> => {
    const urlParams = new URLSearchParams();
    if (params.searchStr) {
      urlParams.append('searchStr', params.searchStr);
    }
    const queryString = urlParams.toString();
    const endpoint = `${this.rootPathV1}/keys${queryString ? `?${queryString}` : ''}`;

    return API.get('billings', endpoint);
  };

  addGovernanceTags = async (params: GovernanceTagParams) => {
    return API.post('billings', this.rootPath, { body: { governanceTagParams: params } });
  };

  updateGovernanceTags = async (params: GovernanceTagParams) => {
    return API.put('billings', this.rootPath, { body: { governanceTagParams: params } });
  };

  deleteGovernanceTags = async (resourceIds: string[]): Promise<BulkDeleteResult[]> => {
    const response = await API.del('billings', this.rootPath, { body: { resourceIds } });
    return response.results;
  };

  getAllResourcesForExport = async (
    queryParams: Omit<FlatResourcesQueryParams, 'page' | 'pageSize'>,
  ): Promise<ResourceData[]> => {
    const params = new URLSearchParams();

    const filterFields = [
      'service',
      'region',
      'resourceName',
      'resourceid',
      'linkedAccountId',
      'customtags',
      'governanceTags',
    ];

    filterFields.forEach((field) => {
      const values = queryParams[field as keyof Omit<FlatResourcesQueryParams, 'page' | 'pageSize'>] as
        | string[]
        | undefined;
      if (values && Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          params.append(field, value);
        });
      }
    });

    if (queryParams.searchText) {
      params.append('searchText', queryParams.searchText);
    }

    if (queryParams.filter) {
      params.append('filter', queryParams.filter);
    }

    if (queryParams.sortBy && queryParams.sortBy.length > 0) {
      params.append('sortBy', JSON.stringify(queryParams.sortBy));
    }

    const queryString = params.toString();
    const endpoint = `${this.rootPath}/resources/export${queryString ? `?${queryString}` : ''}`;

    const response = await API.get('billings', endpoint);

    return response.resources?.map(this.transformResourceData) || [];
  };
}

export default GovernanceTagsApi;
